import { ControlledInput } from '@/components/forms/controlled-input';
import { AuthPane } from '@/components/auth/auth-pane';
import { LegalFooter } from '@/components/auth/legal-footer';
import { PhoneAuthPanel } from '@/components/auth/phone-auth-panel';
import { SocialAuthRow } from '@/components/auth/social-auth-row';
import { Logo } from '@/components/logo';
import { SeoHead } from '@/components/seo-head';
import { ThemedText } from '@/components/themed-text';
import { Button } from '@/components/ui/button';
import { registerSchema, registerStep1Schema, registerStep2Schema } from '@/lib/validators/auth';
import api from '@/services/api';
import { useAuthStore } from '@/store/authStore';
import { showToast } from '@/store/toastStore';
import { USER_ROLE } from '@/types/user-role';
import { Ionicons } from '@expo/vector-icons';
import { zodResolver } from '@hookform/resolvers/zod';
import { Link, useLocalSearchParams } from 'expo-router';
import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useGoogleOAuth } from '@/hooks/use-google-oauth';
import { useAppleOAuth } from '@/hooks/use-apple-oauth';
import { getSupabaseClient } from '@/lib/supabase/supabase';
import {
  Animated,
  Platform,
  Pressable,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
import Reanimated, {
  Easing,
  useAnimatedStyle,
  useSharedValue,
  withSequence,
  withTiming,
  type SharedValue,
} from 'react-native-reanimated';
import { useSafeAreaColorEffect } from '@/hooks/use-safe-area-color';
import { captureAttributionFromUrl } from '@/lib/attribution';
import { getSignupAttribution } from '@/lib/signup-attribution';
import { captureReturnToAppFromUrl } from '@/lib/auth/web-onboarding-return-to-app';
import { trackCompleteRegistration } from '@/lib/meta-pixel';
import { trackRegisterEvent } from '@/lib/register-analytics';
import { z } from 'zod';
import { createThemedStyles, useThemedHex } from '@/hooks/use-theme-colors';
import { FREE_PLAN } from '@/content/facts';
import { pageTitle } from '@/lib/seo/meta';
import { getApiErrorMessage } from '@/utils/api-error';

/** Frase que a API devolve no e-mail duplicado (`api/src/auth/auth.service.ts`, `EMAIL_IN_USE_MESSAGE`). */
const API_EMAIL_IN_USE_MESSAGE = 'Este email já está em uso.';
/** O que a tela mostra no e-mail duplicado. */
const EMAIL_IN_USE_MESSAGE = 'Este e-mail já está em uso.';
const REGISTER_FAILED_MESSAGE = 'Não deu para criar a conta agora. Tente de novo.';

const initialClientProfile = { phone: '' };

/**
 * Etapas do cadastro. O prestador não preenche perfil aqui: título vira o nome,
 * telefone é coletado no onboarding do WhatsApp e a bio fica para o perfil.
 */
type StepKey = 'identity' | 'password' | 'clientProfile';

const useRoleCardStyles = createThemedStyles((th) =>
  StyleSheet.create({
    row: {
      flexDirection: 'row',
      gap: 8,
    },
    card: {
      flex: 1,
      borderRadius: 16,
      borderWidth: 1.5,
      borderColor: th('#E5E5E5'),
      backgroundColor: th('#FFFFFF'),
      paddingVertical: 12,
      paddingHorizontal: 12,
      gap: 6,
    },
    cardSelected: {
      borderColor: '#FD5001',
      backgroundColor: 'rgba(253, 80, 1, 0.06)',
    },
    ring: {
      position: 'absolute',
      top: -1.5,
      left: -1.5,
      right: -1.5,
      bottom: -1.5,
      borderRadius: 16,
      borderWidth: 1.5,
      borderColor: '#FD5001',
      backgroundColor: 'rgba(253, 80, 1, 0.06)',
    },
    header: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 8,
    },
    iconBox: {
      height: 28,
      width: 28,
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: 999,
      backgroundColor: 'rgba(253, 80, 1, 0.1)',
    },
    iconBoxSelected: {
      backgroundColor: '#FD5001',
    },
    roleLabel: {
      flex: 1,
      fontSize: 13,
      fontWeight: '700',
      color: th('#171717'),
    },
    roleLabelSelected: {
      color: '#FD5001',
    },
    mainText: {
      fontSize: 11,
      lineHeight: 14,
      color: th('#737373'),
    },
  }),
);

function RoleOptionCard({
  selected,
  flash,
  iconName,
  roleLabel,
  mainText,
  onPress,
}: {
  selected: boolean;
  /**
   * Progresso 0→1 do realce suave, disparado quando o usuário tenta enviar o
   * código sem ter escolhido. Ignorado no card já selecionado.
   */
  flash?: SharedValue<number>;
  iconName: React.ComponentProps<typeof Ionicons>['name'];
  roleLabel: string;
  mainText: string;
  onPress: () => void;
}) {
  const roleCardStyles = useRoleCardStyles();
  const ringStyle = useAnimatedStyle(() => ({
    opacity: selected ? 0 : (flash?.value ?? 0),
  }));

  return (
    <TouchableOpacity
      activeOpacity={0.85}
      onPress={onPress}
      style={[roleCardStyles.card, selected && roleCardStyles.cardSelected]}
    >
      <Reanimated.View pointerEvents="none" style={[roleCardStyles.ring, ringStyle]} />
      <View style={roleCardStyles.header}>
        <View style={[roleCardStyles.iconBox, selected && roleCardStyles.iconBoxSelected]}>
          <Ionicons name={iconName} size={15} color={selected ? 'white' : '#FD5001'} />
        </View>
        <Text style={[roleCardStyles.roleLabel, selected && roleCardStyles.roleLabelSelected]}>
          {roleLabel}
        </Text>
        {selected && <Ionicons name="checkmark-circle" size={16} color="#FD5001" />}
      </View>
      <Text style={roleCardStyles.mainText}>{mainText}</Text>
    </TouchableOpacity>
  );
}

/** Retorna o primeiro campo que falhou na validação (para analytics) */
function getFirstErrorField(
  fields: string[],
  errorObj: Record<string, unknown>,
): string | undefined {
  for (const field of fields) {
    const value = field
      .split('.')
      .reduce((obj: unknown, key) => (obj as Record<string, unknown>)?.[key], errorObj);
    if (value && typeof value === 'object' && 'message' in value) return field;
  }
  return fields[0];
}

/** Extrai a mensagem de erro de um campo (suporta paths aninhados) */
function getFieldErrorMessage(errors: Record<string, unknown>, field: string): string | undefined {
  const value = field
    .split('.')
    .reduce((obj: unknown, key) => (obj as Record<string, unknown>)?.[key], errors);
  if (
    value &&
    typeof value === 'object' &&
    'message' in value &&
    typeof (value as { message: unknown }).message === 'string'
  ) {
    return (value as { message: string }).message;
  }
  return undefined;
}

function applyZodErrors(
  setError: (name: any, error: { type: string; message: string }) => void,
  issues: z.ZodIssue[],
) {
  for (const issue of issues) {
    const field = issue.path.join('.') || 'root';
    setError(field, { type: 'manual', message: issue.message });
  }
}

function advanceStep(
  currentStep: number,
  setCurrentStep: (step: number) => void,
  slideAnim: Animated.Value,
) {
  slideAnim.setValue(-20);
  Animated.timing(slideAnim, {
    toValue: 0,
    duration: 300,
    useNativeDriver: true,
  }).start();
  setCurrentStep(currentStep + 1);
}

export default function RegisterScreen() {
  return <RegisterFormScreen />;
}

function RegisterFormScreen() {
  const th = useThemedHex();
  const roleCardStyles = useRoleCardStyles();
  const { register, completeProfile, user } = useAuthStore();
  const params = useLocalSearchParams<{ role?: string; mode?: string }>();

  const { signInWithGoogle, isGoogleLoading } = useGoogleOAuth({
    onError: (error) => {
      trackRegisterEvent({
        event: 'register_google_error',
        errorMessage: error instanceof Error ? error.message : String(error),
      });
    },
  });
  const { signInWithApple, isAppleLoading } = useAppleOAuth({
    onError: (error) => {
      trackRegisterEvent({
        event: 'register_apple_error',
        errorMessage: error instanceof Error ? error.message : String(error),
      });
    },
  });
  const isSocialLoading = isGoogleLoading || isAppleLoading;

  useSafeAreaColorEffect(th('#fff'));

  useEffect(() => {
    if (Platform.OS === 'web') {
      captureAttributionFromUrl();
      captureReturnToAppFromUrl();
      trackRegisterEvent({ event: 'register_page_view' });
    }
  }, []);

  const handleGoogleLogin = () => {
    trackRegisterEvent({ event: 'register_google_click' });
    void signInWithGoogle();
  };

  const handleAppleLogin = () => {
    trackRegisterEvent({ event: 'register_apple_click' });
    void signInWithApple();
  };

  const [supabaseSession, setSupabaseSession] = useState<any>(null);

  const isGooglePending = !!supabaseSession && !user;
  const isCompletingProfile = !!user || isGooglePending;
  /** Qual formulário está no lugar: os campos do cadastro, ou o telefone. */
  // WhatsApp é o caminho padrão do cadastro; email fica como alternativa.
  const [method, setMethod] = useState<'email' | 'phone'>('phone');
  /** Realce suave nos cards prestador/cliente ao tentar enviar o código sem escolha. */
  const roleFlash = useSharedValue(0);
  const nudgeRole = () => {
    roleFlash.value = withSequence(
      withTiming(1, { duration: 420, easing: Easing.out(Easing.ease) }),
      withTiming(0, { duration: 900, easing: Easing.inOut(Easing.ease) }),
    );
  };
  const [currentStep, setCurrentStep] = useState(1);
  const [registerError, setRegisterError] = useState<string | null>(null);
  const [fadeAnim] = useState(new Animated.Value(0));
  const [slideAnim] = useState(new Animated.Value(0));
  const [isCheckingEmail, setIsCheckingEmail] = useState(false);

  // Função para verificar se o email já existe
  const checkEmailExists = async (email: string): Promise<boolean> => {
    if (!email || email.length === 0) return false;
    try {
      setIsCheckingEmail(true);
      const response = await api.get(`/user/check-email/${encodeURIComponent(email)}`);
      return response.data.exists;
    } catch (error) {
      console.error('Erro ao verificar email:', error);
      trackRegisterEvent({
        event: 'register_check_email_error',
        step: 1,
        errorMessage: error instanceof Error ? error.message : String(error),
      });
      return false;
    } finally {
      setIsCheckingEmail(false);
    }
  };

  const {
    control,
    handleSubmit,
    watch,
    trigger,
    setError,
    setValue,
    getValues,
    formState: { isSubmitting, errors },
  } = useForm({
    resolver: zodResolver(registerSchema),
    defaultValues: {
      email: user?.email || '',
      password: user ? 'GOOGLE_AUTH' : '',
      confirmPassword: user ? 'GOOGLE_AUTH' : '',
      username: user?.username || '',
      role: undefined,
      providerProfile: undefined,
      clientProfile: undefined,
    },
    mode: 'onBlur',
  });

  useEffect(() => {
    const supabase = getSupabaseClient();
    if (!supabase) return;
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSupabaseSession(session);
      if (session?.user && !user) {
        setValue('email', session.user.email || '');
        setValue('username', session.user.user_metadata?.full_name || '');
        // Pre-fill password to satisfy zod/backend even though it won't be used
        setValue('password', 'GOOGLE_AUTH');
        setValue('confirmPassword', 'GOOGLE_AUTH');
      }
    });
  }, [user, setValue]);

  // Redirect is now handled by _layout.tsx InitialLayout

  // Anima entrada
  useEffect(() => {
    Animated.timing(fadeAnim, {
      toValue: 1,
      duration: 400,
      useNativeDriver: true,
    }).start();
  }, [fadeAnim]);

  // Se recebeu role como parâmetro, define no formulário
  useEffect(() => {
    if (params.role && (params.role === USER_ROLE.PROVIDER || params.role === USER_ROLE.CLIENT)) {
      setValue('role', params.role as USER_ROLE);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.role]);

  // Padrão é WhatsApp; `?mode=email` força o formulário de email/senha.
  useEffect(() => {
    if (params.mode === 'phone') setMethod('phone');
    if (params.mode === 'email') setMethod('email');
  }, [params.mode]);

  const role = watch('role');

  // Prestador: identidade (+ senha). Cliente ganha a etapa do telefone.
  // Quem vem do Google já tem nome/email/senha, então só a identidade sobra.
  const steps: StepKey[] = React.useMemo(() => {
    const list: StepKey[] = ['identity'];
    if (!isCompletingProfile) list.push('password');
    if (role === USER_ROLE.CLIENT) list.push('clientProfile');
    return list;
  }, [isCompletingProfile, role]);

  // Quem chega do Google já tem identidade: telefone não resolve nada aqui.
  const isPhoneMethod = method === 'phone' && !isCompletingProfile;

  const totalSteps = steps.length;
  const stepKey = steps[currentStep - 1] ?? 'identity';

  // Trocar de role encurta a lista (cliente -> prestador): não deixa o índice órfão
  useEffect(() => {
    if (currentStep > totalSteps) setCurrentStep(totalSteps);
  }, [currentStep, totalSteps]);

  useEffect(() => {
    trackRegisterEvent({
      event: 'register_step_viewed',
      step: currentStep,
      role: role ?? undefined,
      isCompletingProfile,
    });
  }, [currentStep, role, isCompletingProfile]);

  useEffect(() => {
    if (role === USER_ROLE.PROVIDER) {
      setValue('clientProfile', undefined);
      setValue('providerProfile', undefined);
    } else if (role === USER_ROLE.CLIENT) {
      setValue('providerProfile', undefined);
      if (!getValues('clientProfile')) {
        setValue('clientProfile', initialClientProfile);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role]);

  const handleNextStep = async () => {
    trackRegisterEvent({
      event: 'register_button_click',
      buttonType: 'next',
      step: currentStep,
      role: role ?? undefined,
      isCompletingProfile,
    });
    setRegisterError(null);

    const formSnapshot = getValues();

    switch (stepKey) {
      case 'identity': {
        const step1Result = registerStep1Schema.safeParse({
          role: formSnapshot.role,
          username: formSnapshot.username ?? '',
          email: formSnapshot.email ?? '',
        });

        if (!step1Result.success) {
          applyZodErrors(setError, step1Result.error.issues);
          setRegisterError(
            step1Result.error.issues[0]?.message ?? 'Preencha o tipo de conta, o nome e o e-mail.',
          );
          return;
        }

        // Quem completa perfil já tem o email cadastrado: checar acusaria duplicado
        const emailValue = formSnapshot.email;
        if (!isCompletingProfile && emailValue?.includes('@') && emailValue.includes('.')) {
          trackRegisterEvent({ event: 'register_check_email_start', step: 1 });
          const emailExists = await checkEmailExists(emailValue);
          if (emailExists) {
            setError('email', { type: 'manual', message: EMAIL_IN_USE_MESSAGE });
            setRegisterError(EMAIL_IN_USE_MESSAGE);
            return;
          }
          trackRegisterEvent({ event: 'register_check_email_ok', step: 1 });
        }

        break;
      }
      case 'password': {
        const step2Result = registerStep2Schema.safeParse({
          password: formSnapshot.password ?? '',
          confirmPassword: formSnapshot.confirmPassword ?? '',
        });

        if (!step2Result.success) {
          applyZodErrors(setError, step2Result.error.issues);
          setRegisterError(step2Result.error.issues[0]?.message ?? 'Confira as senhas.');
          return;
        }
        break;
      }
      case 'clientProfile': {
        const fieldsToValidate = ['clientProfile.phone'];

        const isValid = await trigger(fieldsToValidate as any, { shouldFocus: true });

        if (!isValid) {
          const errorField = getFirstErrorField(
            fieldsToValidate,
            errors as Record<string, unknown>,
          );
          const errorMessage =
            (errorField
              ? getFieldErrorMessage(errors as Record<string, unknown>, errorField)
              : undefined) ?? 'Preencha os dados do perfil.';
          setRegisterError(errorMessage);
          return;
        }

        handleFinalSubmit();
        return;
      }
      default:
        return;
    }

    setRegisterError(null);

    trackRegisterEvent({
      event: 'register_step_completed',
      step: currentStep,
      role: role ?? undefined,
      isCompletingProfile,
    });

    if (currentStep >= totalSteps) {
      handleFinalSubmit();
    } else {
      advanceStep(currentStep, setCurrentStep, slideAnim);
    }
  };

  const handleFinalSubmit = handleSubmit(async (data) => {
    trackRegisterEvent({
      event: 'register_button_click',
      buttonType: 'submit',
      step: currentStep,
      role: data.role,
      isCompletingProfile: !!user,
    });
    setRegisterError(null);

    let isStepFinalValid = true;

    if (!isStepFinalValid) {
      return;
    }

    try {
      const dataToSend = { ...data };
      let metaCompleteRegistrationEventId: string | undefined;
      if (data.role === USER_ROLE.PROVIDER) {
        delete dataToSend.clientProfile;
        Object.assign(dataToSend, getSignupAttribution());
        metaCompleteRegistrationEventId = `provider_registered_${Date.now()}_${Math.random().toString(36).slice(2)}`;
        (dataToSend as any).metaCompleteRegistrationEventId = metaCompleteRegistrationEventId;
      } else if (data.role === USER_ROLE.CLIENT) {
        delete dataToSend.providerProfile;
      }

      if (user) {
        await completeProfile(dataToSend as any);
        trackRegisterEvent({
          event: 'register_success',
          role: data.role,
          step: totalSteps,
          isCompletingProfile: true,
        });
      } else {
        await register(dataToSend as any);
        trackRegisterEvent({
          event: 'register_success',
          role: data.role,
          step: totalSteps,
          isCompletingProfile: false,
        });
        if (data.role === USER_ROLE.PROVIDER && metaCompleteRegistrationEventId) {
          trackCompleteRegistration(metaCompleteRegistrationEventId);
        }
      }
    } catch (error) {
      const errorMessage = getApiErrorMessage(error, REGISTER_FAILED_MESSAGE);

      trackRegisterEvent({
        event: 'register_api_error',
        step: currentStep,
        role: data.role,
        errorMessage: errorMessage,
        httpStatus: (error as { response?: { status?: number } })?.response?.status ?? undefined,
      });
      if (errorMessage === API_EMAIL_IN_USE_MESSAGE) {
        setError('email', { type: 'manual', message: EMAIL_IN_USE_MESSAGE }, { shouldFocus: true });
        setCurrentStep(1);
      } else {
        setRegisterError(errorMessage);
      }
    }
  });

  const handlePreviousStep = () => {
    trackRegisterEvent({
      event: 'register_button_click',
      buttonType: 'back',
      step: currentStep,
      role: role ?? undefined,
      isCompletingProfile,
    });
    if (currentStep <= 1) return;

    setCurrentStep(currentStep - 1);
    setRegisterError(null);
  };

  const RoleSelector = (
    <Controller
      control={control}
      name="role"
      render={({ field: { value, onChange } }) => (
        <View className="mb-5 gap-2">
          <ThemedText className="font-semibold text-sm text-neutral-900">Você é</ThemedText>

          <View style={roleCardStyles.row}>
            <RoleOptionCard
              selected={value === USER_ROLE.PROVIDER}
              flash={roleFlash}
              iconName="business-outline"
              roleLabel="Prestador"
              mainText="Vendo serviços e cobro clientes"
              onPress={() => onChange(USER_ROLE.PROVIDER)}
            />
            <RoleOptionCard
              selected={value === USER_ROLE.CLIENT}
              flash={roleFlash}
              iconName="person-outline"
              roleLabel="Cliente"
              mainText="Contrato serviços e pago"
              onPress={() => onChange(USER_ROLE.CLIENT)}
            />
          </View>
        </View>
      )}
    />
  );

  const IdentityContent = (
    <>
      {RoleSelector}
      <ControlledInput
        control={control}
        name="username"
        label="Nome completo"
        placeholder="Seu nome completo"
        autoComplete="off"
      />
      <ControlledInput
        control={control}
        name="email"
        label="E-mail"
        placeholder="seu@email.com"
        keyboardType="email-address"
        autoCapitalize="none"
        autoComplete="off"
      />
    </>
  );

  const PasswordContent = (
    <>
      <ControlledInput
        control={control}
        name="password"
        label="Senha"
        placeholder="Mínimo 8 caracteres"
        secureTextEntry
        autoComplete="off"
        compact
      />
      <ControlledInput
        control={control}
        name="confirmPassword"
        label="Confirmar senha"
        placeholder="Digite novamente"
        secureTextEntry
        autoComplete="off"
        compact
      />
    </>
  );

  const ClientProfileContent = (
    <>
      <ControlledInput
        control={control}
        name="clientProfile.phone"
        label="Telefone"
        placeholder="(99) 99999-9999"
        keyboardType="phone-pad"
        maskType="phone"
      />
    </>
  );

  const stepContentByKey: Record<StepKey, React.ReactNode> = {
    identity: IdentityContent,
    password: PasswordContent,
    clientProfile: ClientProfileContent,
  };

  const overflowClass =
    Platform.OS === 'web' ? 'overflow-visible min-h-[100dvh]' : 'overflow-hidden';
  return (
    <View className={`flex-1 bg-card ${overflowClass}`}>
      <SeoHead
        title={pageTitle('Criar conta grátis')}
        description="Crie sua conta grátis na Controlapag e organize clientes, agenda e cobranças avulsas. Lembrete no WhatsApp e recebimento pelo link nos planos pagos."
        path="/register"
      />
      <View className={`flex-1 ${overflowClass}`}>
        <View
          className={`flex-1 pt-4 md:mx-auto md:w-full md:max-w-6xl md:flex-row ${overflowClass}`}
        >
          <View className="hidden md:relative md:flex md:flex-1 md:items-center md:justify-center md:p-12">
            <View className="relative z-10 max-w-md">
              <Logo size={64} color="tint" />

              <ThemedText
                role="heading"
                aria-level={1}
                className="mt-6 font-bold text-2xl leading-tight text-neutral-900"
              >
                Comece a gerenciar seus negócios hoje
              </ThemedText>

              <ThemedText className="mt-4 text-base leading-relaxed text-neutral-700">
                Crie sua conta e organize clientes, agenda e cobranças num lugar só.
              </ThemedText>

              <View className="mt-8 gap-4">
                <View className="flex-row items-center gap-3">
                  <View className="h-10 w-10 items-center justify-center rounded-full border border-primary/40 bg-primary/20 dark:bg-primary">
                    <Ionicons className="text-primary dark:text-white" name="checkmark" size={20} />
                  </View>
                  <ThemedText className="flex-1 text-neutral-800">
                    Cadastro rápido e intuitivo
                  </ThemedText>
                </View>

                <View className="flex-row items-center gap-3">
                  <View className="h-10 w-10 items-center justify-center rounded-full border border-primary/40 bg-primary/20 dark:bg-primary">
                    <Ionicons className="text-primary dark:text-white" name="checkmark" size={20} />
                  </View>
                  <ThemedText className="flex-1 text-neutral-800">
                    Para prestadores e clientes
                  </ThemedText>
                </View>

                <View className="flex-row items-center gap-3">
                  <View className="h-10 w-10 items-center justify-center rounded-full border border-primary/40 bg-primary/20 dark:bg-primary">
                    <Ionicons className="text-primary dark:text-white" name="checkmark" size={20} />
                  </View>
                  <ThemedText className="flex-1 text-neutral-800">
                    Grátis com até {FREE_PLAN.maxClientsPerMonth} clientes por mês
                  </ThemedText>
                </View>
              </View>
            </View>
          </View>

          <AuthPane>
            <View className="w-full grow md:max-w-md md:grow-0">
              <View className="flex-1 px-6 pb-6 md:pb-8 md:pt-8">
                <View>
                  <View className="mb-4 items-center">
                    <View className="md:hidden">
                      <Logo size={64} color="tint" />
                    </View>
                    <ThemedText className="mt-2 font-bold text-xl md:mt-0 md:text-2xl">
                      {isCompletingProfile ? 'Completar Perfil' : 'Criar nova conta'}
                    </ThemedText>
                    <ThemedText className="mt-2 text-center text-sm text-gray-500">
                      Preencha os dados abaixo para começar
                    </ThemedText>
                  </View>

                  {registerError && (
                    <View className="mb-3 flex-row items-center gap-2 rounded-xl border border-error-100 bg-error-50 px-4 py-3">
                      <Ionicons className="text-error-600" name="alert-circle" size={18} />
                      <ThemedText className="flex-1 font-medium text-sm text-error-600">
                        {registerError}
                      </ThemedText>
                    </View>
                  )}

                  {isPhoneMethod ? (
                    <View className="gap-4">
                      {RoleSelector}
                      <PhoneAuthPanel
                        role={role === USER_ROLE.CLIENT ? USER_ROLE.CLIENT : USER_ROLE.PROVIDER}
                        onBeforeSend={() => {
                          if (role === USER_ROLE.PROVIDER || role === USER_ROLE.CLIENT) return true;
                          nudgeRole();
                          showToast('Selecione se você é prestador ou cliente.', 'info');
                          return false;
                        }}
                      />
                    </View>
                  ) : (
                    <View className="relative">
                      {steps.map((key, index) => {
                        const isActive = currentStep === index + 1;
                        if (!isActive) return null;

                        return (
                          <Animated.View
                            key={key}
                            style={{
                              opacity: fadeAnim,
                              transform: [
                                {
                                  translateX: slideAnim.interpolate({
                                    inputRange: [-20, 0],
                                    outputRange: [20, 0],
                                    extrapolate: 'clamp',
                                  }),
                                },
                              ],
                            }}
                            className="gap-3"
                          >
                            {stepContentByKey[key]}
                          </Animated.View>
                        );
                      })}
                    </View>
                  )}
                </View>

                <View className="pt-4">
                  {!isPhoneMethod && (
                    <View className="mb-4 flex-row gap-3">
                      {currentStep > 1 && (
                        <Button
                          title="Voltar"
                          onPress={handlePreviousStep}
                          variant="outline"
                          size="md"
                          className="flex-1"
                        />
                      )}

                      {currentStep < totalSteps ? (
                        <Button
                          title={isCheckingEmail ? 'Verificando...' : 'Próximo'}
                          onPress={handleNextStep}
                          size="md"
                          disabled={isSubmitting || isCheckingEmail}
                          className="flex-1"
                        />
                      ) : (
                        <Button
                          title={
                            isSubmitting
                              ? 'CRIANDO...'
                              : isCheckingEmail
                                ? 'Verificando...'
                                : 'FINALIZAR'
                          }
                          onPress={handleNextStep}
                          disabled={isSubmitting || isCheckingEmail}
                          size="md"
                          className="flex-1"
                        />
                      )}
                    </View>
                  )}

                  {!isCompletingProfile && currentStep === 1 && (
                    <>
                      <View className="mb-3 flex-row items-center">
                        <View className="h-[1px] flex-1 bg-neutral-200" />
                        <ThemedText className="mx-4 font-bold text-xs uppercase text-neutral-500">
                          Ou
                        </ThemedText>
                        <View className="h-[1px] flex-1 bg-neutral-200" />
                      </View>

                      <View className="mb-4">
                        <SocialAuthRow
                          intent="signup"
                          mode={method}
                          onWhatsApp={() => setMethod('phone')}
                          onEmail={() => setMethod('email')}
                          onGoogle={handleGoogleLogin}
                          onApple={handleAppleLogin}
                          isGoogleLoading={isGoogleLoading}
                          isAppleLoading={isAppleLoading}
                          disabled={isSubmitting || isSocialLoading}
                        />
                      </View>
                    </>
                  )}

                  <View className="flex-row items-center justify-center">
                    <ThemedText className="text-sm text-gray-500">Já tem uma conta? </ThemedText>
                    <Link href="/login" asChild>
                      <Pressable>
                        <ThemedText className="font-bold text-sm text-primary">
                          Faça login
                        </ThemedText>
                      </Pressable>
                    </Link>
                  </View>
                </View>
              </View>
            </View>
          </AuthPane>
        </View>
      </View>

      <LegalFooter action="continuar" />
    </View>
  );
}
